import localForage from "localforage"
import preval from "preval.macro"
import { ReactNode, useEffect } from "react"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { useAppDispatch, useAppSelector } from "@/App"
import { Site } from "@/sites"
import { useSockets } from "@/sockets"
import { getClients } from "@/store/clients"
import { getDrivers, selectDriversStatus } from "@/store/drivers"
import { setCurrentSite, setDate } from "@/store/global"
import { getNotifications } from "@/store/notifications"
import { getOrders, selectOrdersStatus } from "@/store/orders"
import { getTeams, selectCurrentTeam, selectTeams, setCurrentMultiTeams, setCurrentTeam } from "@/store/teams"
import { getTeamStats } from "@/store/teamsStats"
import {
  setAboutPanel,
  setDriversPanelTab,
  setFocusedDriver,
  setShowDriverDetails,
  setShowOrderDetails,
  setTeamsModalVisibility,
} from "@/store/ui"
import { requestCurrentUser, selectIsAuthenticated, setIsAuthenticated } from "@/store/user"
import { Team } from "@/types"
import { retrieveSavedToken, sessionForage } from "@/utils"
import { DriversPanelTab } from "@/views/MapView/partials/Drivers/DriversPanel"

export const GlobalState = ({ children }: { children: ReactNode }) => {
  useBuildTimestamp()

  useSetCurrentSite()

  useSetCurrentUserAndTeams()

  useSetDefaultSingleTeam()
  useSetDefaultMultiTeams()

  useParamsData()

  useFetchSingleTeamData()
  useFetchMultiTeamData()

  useSockets()

  return <>{children}</>
}

function useBuildTimestamp() {
  useEffect(() => {
    const buildTimestamp = preval`module.exports = Date.now();`

    localForage.setItem("build_timestamp", buildTimestamp)
  }, [])
}

function useSetCurrentSite() {
  const sites = useAppSelector((state) => state.global.sites, shallowEqual)
  const dispatch = useDispatch()

  useEffect(() => {
    async function retrieveAndSetDefaultSite() {
      const savedSite = await localForage.getItem<Site>("current_site")

      if (savedSite) {
        dispatch(setCurrentSite(savedSite))
      } else {
        dispatch(setCurrentSite(sites[0]))
      }
    }

    retrieveAndSetDefaultSite()
  }, [dispatch, sites])
}

function useSetCurrentUserAndTeams() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const dispatch = useDispatch()

  useEffect(() => {
    async function retrieveTokenAndAuthenticateUser() {
      const savedToken = await retrieveSavedToken()

      if (savedToken) {
        batch(() => {
          dispatch(setIsAuthenticated(true))
          dispatch(requestCurrentUser())
          dispatch(getTeams())
        })
      } else {
        dispatch(setIsAuthenticated(false))
      }
    }

    retrieveTokenAndAuthenticateUser()
  }, [dispatch, isAuthenticated])
}

function useSetDefaultSingleTeam() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const teams = useAppSelector(selectTeams, shallowEqual)
  const currentTeam = useAppSelector(selectCurrentTeam, shallowEqual)

  const isTeamSelected = Boolean(currentTeam)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!isAuthenticated || !teams.length) return

    const params = new URLSearchParams(window.location.search)

    function setTeamFromParam() {
      const team_id = Number(params.get("team_id"))
      const team = teams.find((team) => team.id === team_id)

      if (team) {
        dispatch(setCurrentTeam(team))
        params.delete("team_id")
        setRemainingParams(params)
      } else {
        // Team wasn't found, so it doesn't make sense to use any params
        setRemainingParams(new URLSearchParams())
      }
    }

    async function retrieveAndSetSingleTeam() {
      const savedSessionTeam = sessionForage.getItem<Team>("team")

      if (savedSessionTeam) {
        const isTeamFound = teams.find((team) => team.uuid === savedSessionTeam.uuid)

        if (isTeamFound) {
          return dispatch(setCurrentTeam(savedSessionTeam))
        }
      }

      const savedTeam = await localForage.getItem<Team>("team")

      if (savedTeam) {
        const isTeamFound = teams.find((team) => team.uuid === savedTeam.uuid)

        if (isTeamFound) {
          sessionForage.setItem("team", savedTeam)
          return dispatch(setCurrentTeam(savedTeam))
        }
      }

      return dispatch(setTeamsModalVisibility(true))
    }

    if (params.has("team_id")) {
      setTeamFromParam()
    } else if (!isTeamSelected) {
      retrieveAndSetSingleTeam()
    }
  }, [dispatch, isAuthenticated, teams, isTeamSelected])
}

function useSetDefaultMultiTeams() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const teams = useAppSelector(selectTeams, shallowEqual)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!isAuthenticated || !teams.length) return

    async function retrieveAndSetMultiTeams() {
      const savedMultiTeams = await localForage.getItem<Team[]>("multi_teams")

      if (savedMultiTeams) {
        // Filter out teams that don't exist anymore, are from a different server etc.
        const filteredTeams = savedMultiTeams.filter((savedTeam) => teams.find((team) => team.uuid === savedTeam.uuid))
        if (filteredTeams.length) {
          dispatch(setCurrentMultiTeams(filteredTeams))
          return
        }
      }

      dispatch(setCurrentMultiTeams(teams.filter((team) => !team.is_test)))
    }

    retrieveAndSetMultiTeams()
  }, [dispatch, isAuthenticated, teams])
}

function useFetchSingleTeamData() {
  const team = useAppSelector(selectCurrentTeam, shallowEqual)
  const date = useAppSelector((state) => state.global.date)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!isAuthenticated || !team) return

    const { abort: abortGetOrders } = dispatch(getOrders({ teamId: team.id, date }))
    const { abort: abortGetDrivers } = dispatch(getDrivers({ teamIds: [team.id], date }))
    const { abort: abortGetClients } = dispatch(getClients([team.id]))
    const { abort: abortGetNotifications } = dispatch(getNotifications([team.uuid]))
    const { abort: abortGetTeamStats } = dispatch(getTeamStats([team.id]))

    return () => {
      abortGetOrders()
      abortGetDrivers()
      abortGetClients()
      abortGetNotifications()
      abortGetTeamStats()
    }
  }, [dispatch, isAuthenticated, team, date])
}

function useFetchMultiTeamData() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const multiTeams = useAppSelector((state) => state.teams.currentMulti, shallowEqual)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!isAuthenticated || !multiTeams.length) return

    batch(() => {
      dispatch(getNotifications(multiTeams.map((team) => team.uuid)))
      dispatch(getTeamStats(multiTeams.map((team) => team.id)))
    })
  }, [dispatch, isAuthenticated, multiTeams])
}

function useParamsData() {
  // team_id param is handled separately in useSetDefaultSingleTeam hook
  const teams = useAppSelector(selectTeams)
  const driversStatus = useAppSelector(selectDriversStatus)
  const ordersStatus = useAppSelector(selectOrdersStatus)

  const dispatch = useAppDispatch()

  const isTeamDataLoaded = driversStatus === "fetched" && ordersStatus === "fetched"

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    function setDateFromParam() {
      const date = params.get("date")
      if (date) {
        dispatch(setDate(date))
        params.delete("date")
      }
    }

    if (params.has("date")) setDateFromParam()

    setRemainingParams(params)
  }, [dispatch, teams])

  useEffect(() => {
    if (!isTeamDataLoaded) return

    const params = new URLSearchParams(window.location.search)

    function showOrderFromParam() {
      const order_id = Number(params.get("order_id"))

      if (order_id) {
        dispatch(setShowOrderDetails({ id: order_id }))
        dispatch(setAboutPanel({ id: order_id, type: "order" }))
        params.delete("order_id")
      }
    }

    function showDriverFromParam() {
      const driver_id = Number(params.get("driver_id"))
      const order_id = Number(params.get("order_id"))

      if (driver_id) {
        dispatch(setShowDriverDetails(driver_id))
        if (!order_id) dispatch(setFocusedDriver(driver_id))
        params.delete("driver_id")
      }
    }

    function setDriversTabFromParam() {
      const drivers_tab = params.get("drivers_tab")

      if (drivers_tab) {
        dispatch(setDriversPanelTab(drivers_tab as DriversPanelTab))
        params.delete("drivers_tab")
      }
    }

    batch(() => {
      if (params.has("order_id")) showOrderFromParam()
      if (params.has("driver_id")) showDriverFromParam()
      if (params.has("drivers_tab")) setDriversTabFromParam()
    })

    setRemainingParams(params)
  }, [dispatch, teams, isTeamDataLoaded])
}

function setRemainingParams(params: URLSearchParams) {
  const anyParamsLeft = Boolean(params.toString().length)

  window.history.pushState(
    {},
    document.title,
    `${window.location.pathname}${anyParamsLeft ? `?${params.toString()}` : ""}`
  )
}
